import {isFeatureEnabled} from 'feature-flags';
import {Label, Segment, Slider, SliderTick, TextInput} from 'platform/components';
import {Box, Hide, HStack, Show, Space, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

interface SourcingSliderProps extends TestIdProps {
  label?: string;
  labelTooltip?: string;
  min: number;
  max: number;
  marks?: SliderTick[];
  step?: number;
  value?: [number, number];
  shouldHideSlider?: boolean;
  hasMaxPlus?: boolean;
  isFormatNumbersDisabled?: boolean;
  segmentOptions?: string[];
  segmentValue?: string;
  onChange: (value: [number, number]) => void;
  onSegmentChange?: (value: string) => void;
}

export function SourcingSlider(props: SourcingSliderProps) {
  const formatNumber = useFormatNumber();
  const isRedesignEnabled = isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN);
  const shouldFormatNumbers = isRedesignEnabled && !props.isFormatNumbersDisabled;
  const formatValue = (value: number) =>
    shouldFormatNumbers ? formatNumber(value, 0) : value.toString();
  const defaultMin = props.value?.[0] ?? props.min;
  const defaultMax = props.value?.[1] ?? props.max;
  const [inputMinValue, setInputMinValue] = useState<string | null>(defaultMin.toString());
  const [inputMaxValue, setInputMaxValue] = useState<string | null>(defaultMax.toString());
  const [isInputMinFocused, setIsInputMinFocused] = useState(false);
  const [isInputMaxFocused, setIsInputMaxFocused] = useState(false);

  const putValueInRange = (value: number) => Math.min(Math.max(value, props.min), props.max);

  const switchValuesIfNeeded = () => {
    if (!isRedesignEnabled && defaultMin > defaultMax) {
      setInputMinValue(defaultMax.toString());
      setInputMaxValue(defaultMin.toString());
      props.onChange([defaultMax, defaultMin]);
    }
  };

  const formatMaxValue = (value: number) =>
    props.hasMaxPlus && value === props.max ? `${formatValue(value)}+` : formatValue(value);

  const handleInputMinChange = (value: string | null) => {
    setInputMinValue(value);

    const parsedValue = isNotNil(value) ? parseInt(value) : NaN;
    props.onChange([isNaN(parsedValue) ? props.min : putValueInRange(parsedValue), defaultMax]);
  };

  const handleInputMinFocus = () => {
    setIsInputMinFocused(true);
    setInputMinValue(defaultMin.toString());
  };

  const handleInputMinBlur = () => {
    setIsInputMinFocused(false);
    switchValuesIfNeeded();
  };

  const handleInputMaxChange = (value: string | null) => {
    setInputMaxValue(value);

    const parsedValue = isNotNil(value) ? parseInt(value) : NaN;
    props.onChange([defaultMin, isNaN(parsedValue) ? props.max : putValueInRange(parsedValue)]);
  };

  const handleInputMaxFocus = () => {
    setIsInputMaxFocused(true);
    setInputMaxValue(defaultMax.toString());
  };

  const handleInputMaxBlur = () => {
    setIsInputMaxFocused(false);
    switchValuesIfNeeded();
  };

  return (
    <VStack>
      <HStack height={props.shouldHideSlider ? undefined : 9} justify="space-between">
        <Label tooltip={props.labelTooltip}>{props.label}</Label>
        <Show when={isNotNil(props.segmentOptions) && isNotNil(props.segmentValue)}>
          <Segment
            options={props.segmentOptions?.map((option) => ({
              label: option,
              value: option,
            }))}
            value={props.segmentValue ?? ''}
            onChange={(value) => {
              props.onSegmentChange?.(value);
            }}
          />
        </Show>
      </HStack>
      <Hide when={props.shouldHideSlider}>
        <Box paddingVertical={1}>
          <Slider
            isRange
            allowMoveStart
            ticks={false}
            step={props.step ?? 1}
            min={props.min}
            max={props.max}
            nonlinearTicks={props.marks}
            value={props.value || [props.min, props.max]}
            onChange={props.onChange}
            data-testid={suffixTestId('slider', props)}
          />
        </Box>
      </Hide>
      <Space vertical={1} />
      <HStack spacing={2} align="center">
        <TextInput
          aria-label={`${props.label} ${i18n.t('entity.sourcing.filters.labels.rangeFrom')}`}
          type={isInputMinFocused || !shouldFormatNumbers ? 'number' : 'text'}
          value={isInputMinFocused ? inputMinValue : formatValue(defaultMin)}
          onChange={handleInputMinChange}
          onFocus={handleInputMinFocus}
          onBlur={handleInputMinBlur}
          data-testid={suffixTestId('sliderMinInput', props)}
        />
        –
        <TextInput
          aria-label={`${props.label} ${i18n.t('entity.sourcing.filters.labels.rangeTo')}`}
          errorMessage={
            isRedesignEnabled && defaultMin > defaultMax
              ? i18n.t('general.errors.mixed.invalidRange')
              : undefined
          }
          type={isInputMaxFocused ? 'number' : 'text'}
          value={isInputMaxFocused ? inputMaxValue : formatMaxValue(defaultMax)}
          onChange={handleInputMaxChange}
          onFocus={handleInputMaxFocus}
          onBlur={handleInputMaxBlur}
          data-testid={suffixTestId('sliderMaxInput', props)}
        />
      </HStack>
    </VStack>
  );
}
